/**
 * useArtifacts — React Query data layer for the Phase 0 artifact editing lifecycle (CR-D).
 *
 * Wraps the CR-B handlers behind the project's shared axios client (`useApiClient`) and the
 * TanStack Query cache. Types mirror the backend contract in `packages/projects/types.ts`
 * (GetArtifactResponse / PendingCommit / LastRejection) so the frontend and backend stay in sync.
 *
 * Endpoints (see specs/phase2/CR-B-artifact-edit-handlers-spec.md):
 *   GET    /api/projects/{projectId}/artifacts/{type}
 *   PATCH  /api/projects/{projectId}/artifacts/{type}
 *   POST   /api/projects/{projectId}/artifacts/{type}/commit-request
 *   POST   /api/projects/{projectId}/artifacts/{type}/commit-request/{reqId}/approve
 *   POST   /api/projects/{projectId}/artifacts/{type}/commit-request/{reqId}/reject
 *   DELETE /api/projects/{projectId}/artifacts/{type}/commit-request   (withdraw)
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import {
  brandedPipelineFor,
  isBrandedType,
  type BrandedPipeline,
} from "@/lib/branded-docs";

// ─── Shared contract types (mirror packages/projects/types.ts) ───────────────

/** The 5 editable Phase 0 artifact types. Matches the artifact_edits CHECK constraint (V018). */
export const ARTIFACT_TYPES = [
  "preliminary_srs",
  "discovery_agenda",
  "project_plan",
  "baseline_jira_backlog",
  "kickoff_deck",
] as const;

export type ArtifactType = (typeof ARTIFACT_TYPES)[number];

/**
 * The three honest answers to "which Phase 0 artifacts exist?".
 * (2026-10-03 P0 fix-batch design §3.3.1, AD-5b / AD-8a.)
 *
 * This is a UNION and not `{ existing?: ArtifactType[]; loading: boolean; error: boolean }` for one
 * reason: `existing` must be UNREACHABLE unless the answer is actually known. TypeScript narrows on
 * `phase`, so `artifacts.existing` does not compile outside the `'ready'` branch. There is no way to
 * write the H-2/H-3 bug — reading a length off an unresolved query set — and have it type-check.
 *
 * ── THE BUG THIS TYPE EXISTS TO MAKE UNREPRESENTABLE ──────────────────────────────────────────
 * `useArtifactStatuses` builds its map from five `useArtifact` queries and only writes a key when
 * `data?.status` is truthy. THREE different situations produce no key, and they are byte-identical
 * in the map:
 *
 *   • loading  — request in flight            (`isPending: true`,  `data: undefined`)
 *   • errored  — request failed               (`isPending: false`, `data: undefined`)  ← persists
 *   • absent   — 404, genuinely not generated (`isSuccess: true`,  `data: null`)
 *
 * A fully generated project whose artifact queries errored (5xx, throttle, token-refresh race, cold
 * ALB) would otherwise render the "not generated yet" prompt with a primary Generate button wired to
 * the NON-ConflictDialog mutation — and a click there overwrites live drafts. The errored case does
 * NOT self-resolve: `useArtifact` sets `retry: false`, so one failed request lands in the error state
 * immediately and stays there. The destructive button would not be a flash; it would be the page.
 *
 * `'ready'` therefore requires all five queries to be `isSuccess`. A 404 IS a success (the queryFn
 * maps it to `data: null`), so a genuinely ungenerated project still reaches `'ready'` with
 * `existing: []` — which is what keeps the "nothing generated yet" state reachable at all.
 *
 * `retry` travels WITH the error phase rather than being a separate `onRetry` prop, for the same
 * reason `existing` travels with `'ready'`: an action that is only valid in one phase should be
 * impossible to call in any other.
 */
export type ArtifactsState =
  | { readonly phase: "loading" }
  | { readonly phase: "error"; readonly retry: () => void }
  | { readonly phase: "ready"; readonly existing: readonly ArtifactType[] };

/** Human-readable tab/label per artifact type. See CR-D §3.1. */
export const ARTIFACT_LABELS: Record<ArtifactType, string> = {
  preliminary_srs: "Preliminary SRS",
  discovery_agenda: "Discovery Agenda",
  project_plan: "Project Plan",
  baseline_jira_backlog: "Jira Backlog",
  kickoff_deck: "Kickoff Deck",
};

/** Draft lifecycle status. Matches the artifact_edits CHECK constraint (V018). */
export type ArtifactStatus =
  | "generated"
  | "draft_edit"
  | "pending_commit"
  | "committed"
  | "rejected";

/** Pending commit request summary — present on the GET response only when status = 'pending_commit'. */
export interface PendingCommit {
  commit_request_id: string;
  commit_message: string;
  requested_by: string;
  created_at: string;
  diff: string;
}

/** Most recent rejection summary — present on the GET response only when status = 'rejected'. */
export interface ArtifactRejection {
  rejection_reason: string;
  rejected_by: string;
  resolved_at: string;
}

/** Response for GET /api/projects/{projectId}/artifacts/{type} (CR-B §2.1). */
export interface ArtifactResponse {
  artifact_edit_id: string;
  artifact_type: string;
  content: string;
  status: ArtifactStatus;
  base_commit_sha: string | null;
  edited_by: string | null;
  updated_at: string;
  pending_commit?: PendingCommit;
  last_rejection?: ArtifactRejection;
}

/** Response for POST .../commit-request (submit). */
export interface SubmitCommitResponse {
  commit_request_id: string;
  diff: string;
  commit_message: string;
}

/** Response for POST .../commit-request/{reqId}/approve. */
export interface ApproveCommitResponse {
  github_commit_sha: string;
  committed_at: string;
}

/** Response for POST .../commit-request/{reqId}/reject. */
export interface RejectCommitResponse {
  rejected: boolean;
  artifact_now_status: ArtifactStatus;
}

/** Variables for the reject mutation. */
export interface RejectCommitVariables {
  reqId: string;
  rejection_reason: string;
}

/** Stable query key for a single artifact. */
export function artifactQueryKey(
  projectId: string,
  artifactType: string,
): [string, string, string] {
  return ["artifact", projectId, artifactType];
}

// ─── Query ───────────────────────────────────────────────────────────────────

/**
 * True only for the backend's "artifact not generated yet" answer: HTTP 404 with
 * `code: 'ARTIFACT_NOT_FOUND'` (get-artifact.ts). Any other 404 (PROJECT_NOT_FOUND from
 * assertProjectAccess, a missing route, …) is NOT an empty state.
 */
export function isArtifactNotFound(err: unknown): boolean {
  const response = (
    err as { response?: { status?: number; data?: { code?: unknown } } }
  )?.response;
  return (
    response?.status === 404 && response.data?.code === "ARTIFACT_NOT_FOUND"
  );
}

/**
 * GET a single artifact's current content + status. Returns null when not yet generated
 * (404 ARTIFACT_NOT_FOUND); any other failure, including a PROJECT_NOT_FOUND 404, is an error.
 */
export function useArtifact(projectId: string, artifactType: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: artifactQueryKey(projectId, artifactType),
    queryFn: async () => {
      try {
        const response = await client.get<ArtifactResponse>(
          `/api/projects/${projectId}/artifacts/${artifactType}`,
          // A 404 here is an EXPECTED answer ("not generated yet"), not a failure, so the
          // global axios interceptor must not toast it. Without this the interceptor fires
          // before the catch below can map 404 → null, and because `useArtifactStatuses`
          // runs this query once per ARTIFACT_TYPES entry, an ungenerated project stacked
          // five "Resource not found" toasts over a correctly rendered empty state.
          // Same opt-out used by useIntakes/useUsers/useSecrets (see lib/api.ts §_suppressErrorToast).
          { _suppressErrorToast: true },
        );
        return response.data;
      } catch (err: unknown) {
        // ONLY `ARTIFACT_NOT_FOUND` means "the artifact_edits row doesn't exist yet" (generation
        // not yet run, or pre-V018). That is mapped to null so the UI shows a graceful "not
        // generated yet" state. Every other 404 — notably `PROJECT_NOT_FOUND`, which is how
        // `assertProjectAccess` reports a membership denial — is a real failure and must reach
        // the error state. Treating it as "not generated" would offer a Generate button to a user
        // who has no access to the project.
        if (isArtifactNotFound(err)) return null;
        throw err;
      }
    },
    enabled: !!projectId && !!artifactType,
    retry: false, // don't retry 404s or other failures — the user can refresh manually
    staleTime: 30_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/**
 * Variables for {@link usePatchArtifact}. A bare string is the legacy, unconditional overwrite
 * (DiffProposal). The object form adds optimistic concurrency: `expectedUpdatedAt` is the
 * `updated_at` the edit was based on; the server answers 409 ARTIFACT_STALE if the row has changed
 * since (see {@link isArtifactStale}). Omit/null it for an explicit overwrite.
 */
export type PatchArtifactVariables =
  | string
  | { content: string; expectedUpdatedAt?: string | null };

/** True for the PATCH lost-update rejection: 409 `ARTIFACT_STALE` (nothing was written). */
export function isArtifactStale(err: unknown): boolean {
  const response = (
    err as { response?: { status?: number; data?: { code?: unknown } } }
  )?.response;
  return response?.status === 409 && response.data?.code === "ARTIFACT_STALE";
}

/** PATCH artifact content (inline edit / accept diff proposal). */
export function usePatchArtifact(projectId: string, artifactType: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: PatchArtifactVariables) => {
      const { content, expectedUpdatedAt } =
        typeof vars === "string"
          ? { content: vars, expectedUpdatedAt: undefined }
          : vars;
      const response = await client.patch<ArtifactResponse>(
        `/api/projects/${projectId}/artifacts/${artifactType}`,
        expectedUpdatedAt
          ? { content, expected_updated_at: expectedUpdatedAt }
          : { content },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: artifactQueryKey(projectId, artifactType),
      });
    },
  });
}

/** POST commit request (submit the current draft for approval). */
export function useSubmitCommitRequest(
  projectId: string,
  artifactType: string,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commitMessage?: string) => {
      const body =
        commitMessage && commitMessage.trim()
          ? { commit_message: commitMessage.trim() }
          : {};
      const response = await client.post<SubmitCommitResponse>(
        `/api/projects/${projectId}/artifacts/${artifactType}/commit-request`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: artifactQueryKey(projectId, artifactType),
      });
    },
  });
}

/** POST approve — commit the pending draft to GitHub. Takes the pending commit request id. */
export function useApproveCommitRequest(
  projectId: string,
  artifactType: string,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reqId: string) => {
      const response = await client.post<ApproveCommitResponse>(
        `/api/projects/${projectId}/artifacts/${artifactType}/commit-request/${reqId}/approve`,
        {},
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: artifactQueryKey(projectId, artifactType),
      });
    },
  });
}

/** POST reject — return the pending draft to 'rejected' with a required reason. */
export function useRejectCommitRequest(
  projectId: string,
  artifactType: string,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reqId, rejection_reason }: RejectCommitVariables) => {
      const response = await client.post<RejectCommitResponse>(
        `/api/projects/${projectId}/artifacts/${artifactType}/commit-request/${reqId}/reject`,
        { rejection_reason },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: artifactQueryKey(projectId, artifactType),
      });
    },
  });
}

/** DELETE the pending commit request (withdraw) — returns the artifact to 'draft_edit'. */
export function useWithdrawCommitRequest(
  projectId: string,
  artifactType: string,
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await client.delete(
        `/api/projects/${projectId}/artifacts/${artifactType}/commit-request`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: artifactQueryKey(projectId, artifactType),
      });
    },
  });
}

// ─── Aggregate imperative API (spec §4) ──────────────────────────────────────

/** Stable set of imperative async calls for a project's artifacts. Function references are
 *  memoized on the axios client so they can be listed safely in effect/callback deps. */
export function useArtifacts(projectId: string) {
  const client = useApiClient();

  return useMemo(
    () => ({
      getArtifact: async (type: string): Promise<ArtifactResponse> => {
        const response = await client.get<ArtifactResponse>(
          `/api/projects/${projectId}/artifacts/${type}`,
        );
        return response.data;
      },

      patchArtifact: async (
        type: string,
        content: string,
      ): Promise<ArtifactResponse> => {
        const response = await client.patch<ArtifactResponse>(
          `/api/projects/${projectId}/artifacts/${type}`,
          { content },
        );
        return response.data;
      },

      submitCommitRequest: async (
        type: string,
        commitMessage?: string,
      ): Promise<SubmitCommitResponse> => {
        const body =
          commitMessage && commitMessage.trim()
            ? { commit_message: commitMessage.trim() }
            : {};
        const response = await client.post<SubmitCommitResponse>(
          `/api/projects/${projectId}/artifacts/${type}/commit-request`,
          body,
        );
        return response.data;
      },

      approveCommitRequest: async (
        type: string,
        reqId: string,
      ): Promise<ApproveCommitResponse> => {
        const response = await client.post<ApproveCommitResponse>(
          `/api/projects/${projectId}/artifacts/${type}/commit-request/${reqId}/approve`,
          {},
        );
        return response.data;
      },

      rejectCommitRequest: async (
        type: string,
        reqId: string,
        rejectionReason: string,
      ): Promise<RejectCommitResponse> => {
        const response = await client.post<RejectCommitResponse>(
          `/api/projects/${projectId}/artifacts/${type}/commit-request/${reqId}/reject`,
          { rejection_reason: rejectionReason },
        );
        return response.data;
      },

      withdrawCommitRequest: async (type: string): Promise<void> => {
        await client.delete(
          `/api/projects/${projectId}/artifacts/${type}/commit-request`,
        );
      },
    }),
    [client, projectId],
  );
}

// ─── Branded document downloads (Phase 0 branded DOCX / PPTX) ────────────────

/** Response for GET .../branded-docs/download?type={srs|pptx} — a short-lived presigned S3 URL. */
export interface BrandedDocDownloadResponse {
  download_url: string;
  expires_in: number;
}

/** GET a presigned download URL for a branded Phase 0 document (SRS DOCX or Kickoff PPTX). */
export function useDownloadBrandedDoc(projectId: string) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (type: "srs" | "pptx") => {
      const response = await client.get<BrandedDocDownloadResponse>(
        `/api/projects/${projectId}/branded-docs/download?type=${type}`,
      );
      return response.data;
    },
  });
}

/** Stable query key for the presigned URL the branded PREVIEW iframe renders. */
export function brandedDocUrlQueryKey(
  projectId: string,
  pipeline: BrandedPipeline,
): [string, string, string] {
  return ["branded-doc-url", projectId, pipeline];
}

/**
 * GET the presigned URL for the branded preview, as a CACHE-KEYED QUERY.
 *
 * ── WHY THIS IS A QUERY AND NOT THE DOWNLOAD MUTATION ──────────────────────────────────────────
 * `BrandedDocPane`'s preview used to call {@link useDownloadBrandedDoc} (a mutation, so uncached)
 * from an effect guarded by a `loadedFor` ref that fired ONCE per mount. That made the visible
 * iframe unrefreshable: a successful regeneration had no way to replace the URL it was showing, so
 * even the pane's own Regenerate button left a stale document on screen until the component
 * remounted. Nothing invalidatable existed to invalidate.
 *
 * As a query, the URL has an identity. {@link useRegenerateBrandedDoc} invalidates that identity,
 * the query refetches, a NEW presigned URL arrives, and the iframe — keyed on the URL — remounts
 * with the fresh render. The mount-once ref guard is gone, and with it the bug.
 *
 * `staleTime` (10 min) is kept well under the presigned-URL lifetime (12 h — the server reports it
 * as `expires_in`) so a cached entry can never be handed to the viewer after it has expired.
 */
export function useBrandedDocUrl(
  projectId: string,
  pipeline: BrandedPipeline,
  enabled: boolean,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: brandedDocUrlQueryKey(projectId, pipeline),
    queryFn: async () => {
      const response = await client.get<BrandedDocDownloadResponse>(
        `/api/projects/${projectId}/branded-docs/download?type=${pipeline}`,
      );
      return response.data.download_url;
    },
    enabled: !!projectId && enabled,
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
}

/** POST-less trigger: GET .../branded-docs/download (no type) kicks off branded doc generation. */
export function useTriggerBrandedDocs(projectId: string) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async () => {
      const response = await client.get<{
        generated: boolean;
        srs_key: string | null;
        pptx_key: string | null;
      }>(`/api/projects/${projectId}/branded-docs/download`);
      return response.data;
    },
  });
}

/** Response for POST .../branded-docs/download?type={srs|pptx} — regenerate + fresh presigned URL. */
export interface BrandedRegenResponse {
  regenerated: boolean;
  download_url: string;
}

/** Variables for {@link useTriggerBrandedRegenerate}. */
export interface BrandedRegenerateVariables {
  type: "srs" | "pptx";
  /**
   * Optional abort signal. Aborting stops the CLIENT waiting for the response and lets the UI
   * leave its "regenerating" state.
   *
   * LIMITATION: there is no server-side cancel endpoint for branded-doc regeneration — it is a
   * synchronous POST, not a tracked job (contrast `POST /phase0/stop`, which cancels the Phase 0
   * generation job row). Aborting therefore does NOT stop the server; the document may still be
   * regenerated in the background. Surface that caveat wherever the cancel control is rendered.
   */
  signal?: AbortSignal;
  /**
   * Opt out of the global axios error toast. Set by background callers (chat accept) that show
   * their own, more specific message — otherwise a failed render after a SUCCESSFUL accept would
   * surface as a generic "Server error. Please try again.", which reads as if the accept failed.
   */
  suppressErrorToast?: boolean;
}

/**
 * POST .../branded-docs/download?type={srs|pptx} — regenerate ONLY that branded document from the
 * latest saved markdown and return a fresh 12-hour presigned URL. Used by `BrandedDocPane` to
 * refresh the preview after an inline/chat markdown save.
 */
export function useTriggerBrandedRegenerate(projectId: string) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async ({
      type,
      signal,
      suppressErrorToast,
    }: BrandedRegenerateVariables) => {
      const response = await client.post<BrandedRegenResponse>(
        `/api/projects/${projectId}/branded-docs/download?type=${type}`,
        {},
        suppressErrorToast ? { signal, _suppressErrorToast: true } : { signal },
      );
      return response.data;
    },
  });
}

/** Variables for {@link useRegenerateBrandedDoc}. */
export interface RegenerateBrandedDocVariables {
  /** Any artifact type. Non-branded types are a no-op, not an error — see the hook docstring. */
  artifactType: string;
  /** Client-side abort. Same server-side limitation as {@link BrandedRegenerateVariables}. */
  signal?: AbortSignal;
  /** See {@link BrandedRegenerateVariables.suppressErrorToast}. */
  suppressErrorToast?: boolean;
}

/** Outcome of {@link useRegenerateBrandedDoc}. `pipeline` is present only when work was done. */
export interface RegenerateBrandedDocOutcome {
  regenerated: boolean;
  pipeline?: BrandedPipeline;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE PATH that re-renders a branded document after its markdown changes.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── THE BUG THIS HOOK EXISTS TO CLOSE ──────────────────────────────────────────────────────────
 * Branded regeneration used to be reachable only through an imperative callback that
 * `BrandedDocPane` handed UPWARD to `FileContentView` (`onRegenerateRef` → `regenRef.current`).
 * Two failures followed from that shape, and neither was recoverable without restructuring:
 *
 *   1. `BrandedDocPane` renders only while `viewMode === 'branded'`. A user who had never opened
 *      the Branded tab left `regenRef.current` null, so `regenRef.current?.()` after an inline
 *      save was a silent no-op — the optional-chain swallowed it. The branded DOCX then diverged
 *      from the markdown with nothing on screen saying so.
 *   2. Only `MarkdownLiveEditor.onSaved` was wired to it at all. A chat-accepted edit
 *      (`useResolveProjectChatResult`, `data.applied === true`) invalidated the markdown artifact
 *      query and stopped there, so the branded doc NEVER regenerated from the chat path — the
 *      primary editing surface.
 *
 * Making this a mutation on the shared query client removes the precondition entirely: callers do
 * not need the pane mounted, do not need a ref, and do not need to know which pipeline an artifact
 * maps to. `FileContentView` (inline save), `useResolveProjectChatResult` (chat accept) and
 * `BrandedDocPane` (manual Regenerate) all call THIS, so there is one path and one set of
 * invalidations rather than three partially-wired ones.
 *
 * ── WHY A NON-BRANDED TYPE IS A NO-OP RATHER THAN AN ERROR ─────────────────────────────────────
 * Callers sit on generic code paths — a chat accept resolves whichever of the five artifact types
 * the agent touched, and three of them have no branded rendering. Pushing the `isBrandedType`
 * check in here is what lets those call sites stay unconditional: they always ask, and the hook
 * answers `{ regenerated: false }` when there is nothing to re-render. A throw would force every
 * caller to re-derive the same predicate, which is how the two paths drifted apart originally.
 *
 * On success, BOTH branded caches are invalidated: the preview query (`brandedPreviewQueryKey`)
 * and the presigned-URL query the visible iframe renders (`brandedDocUrlQueryKey`). Invalidating
 * only the former was the secondary half of this bug — see {@link useBrandedDocUrl}.
 */
export function useRegenerateBrandedDoc(projectId: string) {
  const { mutateAsync: regenerate } = useTriggerBrandedRegenerate(projectId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      artifactType,
      signal,
      suppressErrorToast,
    }: RegenerateBrandedDocVariables): Promise<RegenerateBrandedDocOutcome> => {
      if (!isBrandedType(artifactType)) return { regenerated: false };
      const pipeline = brandedPipelineFor(artifactType);
      await regenerate({
        type: pipeline,
        signal,
        ...(suppressErrorToast ? { suppressErrorToast: true } : {}),
      });
      return { regenerated: true, pipeline };
    },
    onSuccess: async (outcome) => {
      if (!outcome.regenerated || !outcome.pipeline) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: brandedPreviewQueryKey(projectId, outcome.pipeline),
        }),
        queryClient.invalidateQueries({
          queryKey: brandedDocUrlQueryKey(projectId, outcome.pipeline),
        }),
      ]);
    },
  });
}

// ─── In-browser branded previews (SRS HTML / Kickoff PPTX markdown) ──────────

/** Stable query key for a branded in-browser preview. */
export function brandedPreviewQueryKey(
  projectId: string,
  kind: "srs" | "pptx",
): [string, string, string] {
  return ["branded-preview", projectId, kind];
}

/** Response for GET .../branded-docs/preview?type=pptx&action=preview — raw slide markdown. */
export interface PptxMarkdownResponse {
  markdown: string;
}

/**
 * GET .../branded-docs/preview?type=srs&action=preview — a self-contained HTML rendering of the
 * branded SRS, returned as `text/html`. Used as an iframe `srcDoc` for in-browser preview instead
 * of the Office Online embed. Disabled until `enabled` is true (e.g. the Preview tab is active).
 */
export function useFetchSrsHtmlPreview(projectId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: brandedPreviewQueryKey(projectId, "srs"),
    queryFn: async () => {
      const res = await client.get<string>(
        `/api/projects/${projectId}/branded-docs/preview?type=srs&action=preview`,
        { responseType: "text" },
      );
      return res.data;
    },
    enabled: !!projectId && enabled,
    staleTime: 10 * 60 * 1000, // 10 min — regeneration invalidates this key explicitly
    retry: false,
  });
}

/**
 * GET .../branded-docs/preview?type=pptx&action=preview — the Kickoff Deck slide content as
 * markdown. Parsed client-side into slide cards by {@link KickoffSlidePreview}. Disabled until
 * `enabled` is true (e.g. the Preview tab is active on a kickoff_deck artifact).
 */
export function useFetchPptxMarkdown(projectId: string, enabled: boolean) {
  const client = useApiClient();
  return useQuery({
    queryKey: brandedPreviewQueryKey(projectId, "pptx"),
    queryFn: async () => {
      const res = await client.get<PptxMarkdownResponse>(
        `/api/projects/${projectId}/branded-docs/preview?type=pptx&action=preview`,
      );
      return res.data.markdown;
    },
    enabled: !!projectId && enabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

// ─── Version history (2026-10-03 P0 fix-batch design §5.5, AD-10 / AD-9a) ────

/**
 * One approved commit of an artifact. `version_no` is a presentation ordinal computed server-side
 * over `resolved_at ASC`; it is never stored, because storing it would create a second source of
 * truth that could disagree with row order.
 */
export interface ArtifactVersion {
  version_no: number;
  id: string;
  commit_message: string;
  requested_by: string;
  approved_by: string | null;
  github_commit_sha: string | null;
  resolved_at: string;
  /** Present only on a single-version fetch — the list response omits it (OQ-C). */
  diff?: string;
}

export interface ArtifactVersionsResponse {
  artifact_type: string;
  versions: ArtifactVersion[];
}

export const artifactVersionsQueryKey = (
  projectId: string,
  artifactType: string,
): readonly unknown[] => ["artifact-versions", projectId, artifactType];

/**
 * GET the approved commit history for one artifact, newest first.
 *
 * TWO consumers, and the second is the non-obvious one:
 *   1. `FileContentView`'s `Version history (n)` disclosure — makes the committed banner's promise
 *      that "the committed version stays in the project's history" verifiable rather than a claim.
 *   2. `MarkdownLiveEditor`'s committed banner reads **row 1** for `approved_by` and
 *      `github_commit_sha` (AD-9a). `get-artifact.ts` cannot supply those: it attaches
 *      commit-request data only for rows with `status = 'pending'`, and `approved_by` lives on the
 *      APPROVED row, which that query excludes by construction. `edited_by` is the last EDITOR, not
 *      the approver — using it would attribute the sign-off to the wrong person, which on an
 *      approval control is a correctness defect, not a cosmetic one.
 *
 * Why this and not two additive fields on the artifact GET: that GET is the hottest read path in the
 * workspace (it runs five times per page load) and would need a second query to serve a banner that
 * only renders for `status === 'committed'`. It would also create two sources for the same SHA —
 * `artifact_edits.base_commit_sha` and the latest approved request's `github_commit_sha` are
 * maintained by the same transaction, so returning both invites a consumer to compare them and act
 * on a difference that can only ever be a bug.
 *
 * `enabled` is caller-controlled so the five page-level artifact queries do not each drag a versions
 * query along with them.
 */
export function useArtifactVersions(
  projectId: string,
  artifactType: string,
  enabled = true,
) {
  const client = useApiClient();

  return useQuery({
    queryKey: artifactVersionsQueryKey(projectId, artifactType),
    queryFn: async (): Promise<ArtifactVersion[]> => {
      const response = await client.get<ArtifactVersionsResponse>(
        `/api/projects/${projectId}/artifacts/${artifactType}/versions`,
      );
      return response.data.versions;
    },
    enabled: !!projectId && !!artifactType && enabled,
    // Approved versions are immutable once written, so this can be cached hard. Invalidated
    // explicitly by the approve mutation.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
